/**
 * NotificationBadge Component
 *
 * Displays notification count badges:
 * - Red badge for notifications/alerts
 * - Handles 99+ overflow gracefully
 * - Small variant for compact display
 * - Can be positioned absolutely or inline
 *
 * Renamed from Badge.jsx (SCRUM-901) to avoid collision
 * with the new categorical Badge component.
 *
 * Epic: MVP 1.21.2 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @component
 * @param {Object} props
 * @param {number} props.count - Count to display (required)
 * @param {boolean} props.small - Use small variant (default: false)
 * @param {boolean} props.dot - Show as dot only without count (default: false)
 * @param {string} props.color - Badge color (default: 'error')
 */

import React from 'react';
import PropTypes from 'prop-types';

const NotificationBadge = ({
  count,
  small = false,
  dot = false,
  color = 'error'
}) => {
  // Don't render if count is 0 or undefined
  if (!count || count <= 0) {
    return null;
  }

  // Format count (99+ for overflow)
  const displayCount = count > 99 ? '99+' : count;

  // Get color variable based on prop
  const getColor = () => {
    switch (color) {
      case 'error':
        return 'var(--color-error)';
      case 'warning':
        return 'var(--color-warning)';
      case 'success':
        return 'var(--color-success)';
      case 'info':
        return 'var(--color-info)';
      case 'primary':
        return 'var(--color-primary)';
      default:
        return 'var(--color-error)';
    }
  };

  if (dot) {
    return (
      <div
        style={{
          ...styles.dot,
          backgroundColor: getColor(),
        }}
      />
    );
  }

  return (
    <div
      style={{
        ...styles.badge,
        ...(small && styles.badgeSmall),
        backgroundColor: getColor(),
      }}
    >
      {displayCount}
    </div>
  );
};

const styles = {
  badge: {
    minWidth: 'var(--nav-badge-size)',
    height: 'var(--nav-badge-size)',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-error)',
    color: 'var(--color-white)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-bold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 var(--space-1)',
    lineHeight: 1,
  },

  badgeSmall: {
    minWidth: '14px',
    height: '14px',
    fontSize: '10px',
    padding: '0 3px',
  },

  dot: {
    width: 'var(--notification-badge-size)',
    height: 'var(--notification-badge-size)',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-error)',
  },
};

NotificationBadge.propTypes = {
  count: PropTypes.number.isRequired,
  small: PropTypes.bool,
  dot: PropTypes.bool,
  color: PropTypes.oneOf(['error', 'warning', 'success', 'info', 'primary']),
};

export default NotificationBadge;
